import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AiOutlineUser, AiOutlineSetting, AiOutlineLogout } from 'react-icons/ai';
import { signOut } from '../lib/supabase/auth';

/**
 * ProfileDropdown component for user profile actions
 * 
 * @param {string} userName - The user's display name
 * @param {string} userEmail - The user's email address
 * @param {Object} userDetails - User details including preferred name, first name, etc.
 */
const ProfileDropdown = ({ userName, userEmail, userDetails = {} }) => {
  const navigate = useNavigate();
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [signOutLoading, setSignOutLoading] = useState(false);
  const profileDropdownRef = useRef(null);
  
  // Get the first letter for the user avatar
  const getUserInitial = () => {
    // Use preferred name if available
    if (userDetails?.preferredName) {
      return userDetails.preferredName.charAt(0).toUpperCase();
    }
    
    // Fall back to first name if available
    if (userDetails?.firstName) {
      return userDetails.firstName.charAt(0).toUpperCase();
    }
    
    // Use first letter of username if neither preferred name nor first name are available
    if (userName) {
      return userName.charAt(0).toUpperCase();
    }
    
    // Default fallback
    return 'U';
  };
  
  // Generate a random but consistent color based on the user's name
  const getAvatarColor = () => {
    const colorSeed = userEmail || userName || 'default';
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
      'bg-pink-500', 'bg-yellow-500', 'bg-red-500', 
      'bg-indigo-500', 'bg-teal-500'
    ];
    
    // Simple hash to pick a consistent color
    let hash = 0;
    for (let i = 0; i < colorSeed.length; i++) {
      hash = colorSeed.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    return colors[Math.abs(hash) % colors.length];
  };
  
  // Close profile dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    }
    
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [profileDropdownRef]);
  
  // Handle sign out
  const handleSignOut = async () => {
    setSignOutLoading(true);
    try {
      await signOut();
      // The signOut function now handles the redirect, so we don't need to do anything else
    } catch (err) {
      console.error('Sign out error:', err);
      setSignOutLoading(false);
      setProfileDropdownOpen(false);
    }
  };
  
  return (
    <div className="relative" ref={profileDropdownRef}>
      <button 
        onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
        className="focus:outline-none"
        aria-label="Open profile menu"
      >
        <div 
          className="profile-dropdown__avatar w-10 h-10 rounded-full flex items-center justify-center font-medium cursor-pointer border-2 text-black border-black hover:border-gray-400"
        >
          {getUserInitial()}
        </div>
      </button>
      
      {/* Profile Dropdown */}
      {profileDropdownOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-30">
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-sm leading-5 font-medium text-gray-900">
              {userDetails?.preferredName || userName}
            </p>
            <p className="text-xs leading-4 font-medium text-gray-500 mt-1">{userEmail}</p>
          </div>
          <Link 
            to="/profile" 
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            onClick={() => setProfileDropdownOpen(false)}
          >
            <AiOutlineUser className="mr-2" />
            Your Profile
          </Link>
          <Link 
            to="/settings" 
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            onClick={() => setProfileDropdownOpen(false)}
          >
            <AiOutlineSetting className="mr-2" />
            Settings
          </Link>
          <button 
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            onClick={handleSignOut}
            disabled={signOutLoading}
          >
            {signOutLoading ? (
              <span className="inline-block w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin mr-2"></span>
            ) : (
              <AiOutlineLogout className="mr-2" />
            )}
            {signOutLoading ? 'Signing Out...' : 'Sign Out'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ProfileDropdown; 